import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 5.1 visual preview at /preview/lesson/5.1
 * "The cover letter and the personal statement"
 * Week 5 opener: take the chain into writing where the reader has to choose you.
 */

export default function LessonPreviewPage5_1() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromWeek4 />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 5 · Taking the chain into the wild · Lesson 5.1
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The cover letter and the personal statement
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 45 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Stakes · Reader · Particulars</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 paragraph-picker drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="Two applicants. Same school. Same internship. Different paragraph.">
      <p className="mt-6 text-text/90">
        Two students apply for the same summer internship at a small architecture
        firm. Same school. Same GPA. Same robotics club. Both used AI to draft a
        cover letter. Here is the opening paragraph each one shipped.
      </p>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Applicant A
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;I am writing to express my strong interest in the summer
            internship at your firm. I am a hardworking and motivated student with
            a passion for architecture and design. I believe my skills and
            dedication would make me a valuable addition to your team.&rdquo;
          </p>
        </div>
        <div className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
            Applicant B
          </p>
          <p className="mt-3 text-text/90 italic">
            &ldquo;Last spring I spent four Saturdays in a row at the construction
            site behind Whole Foods on Greenville, watching the brick crew lay the
            first three feet of the new library. I want to spend this summer
            learning how the drawings on your wall become that wall.&rdquo;
          </p>
        </div>
      </div>

      <p className="mt-6 text-text/90">
        Both paragraphs were polished by AI. Both are grammatically clean. One of
        them gets read. One gets skimmed and stacked.
      </p>
      <p className="mt-4 text-text/90">
        The difference is not effort. The difference is that Applicant B did the
        five moves before AI did the polish. Applicant A let AI write the whole
        thing. Week 5 is about doing the five moves at the moments that count
        most: when a reader has to choose you over someone else.
      </p>
    </Section>
  );
}

/* --- Section 2: From Week 4 --- */
function FromWeek4() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Week 4" title="The chain plus the five moves. Now the stakes change.">
      <p className="mt-6 text-text/90">
        Week 4 named what only you can do: decide what matters, notice what is
        specific, connect what does not obviously connect, defend what you
        shipped, care about who reads it. Week 4 ran those moves on school work.
      </p>
      <p className="mt-4 text-text/90">
        Week 5 runs them on writing that lives outside school. The mechanics do
        not change. The reader does. The cost of a flat paragraph goes up.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="Name the five moves only you can make." />
        <ReviewItem n="02" q="Define Judgment in one sentence." />
        <ReviewItem n="03" q="The diagnostic when work feels flat: what four questions do you read aloud?" />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90">{q}</span>
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The reader picks one" title="When the reader has to choose, AI averages will lose to one specific second.">
      <Subhead n={1} title="The vocab for the week" />
      <p className="mt-4 text-text/90">
        Week 5 has one word that holds all five lessons together. Without it, the
        whole week reads like generic writing advice. With it, the moves become
        sharper because the cost of skipping them gets clearer.
      </p>
      <VocabCard
        term="Stakes"
        definition="What the reader can give you or take from you. High stakes mean the reader has to choose: choose you, choose your idea, choose to act. Stakes change what counts as a good paragraph because they change what the reader is looking for."
        analogy="A free throw in practice and a free throw with one second left are the same physical motion. They are not the same shot. The mechanics carry over. The stakes change which mechanics you trust. Writing is the same: a journal entry and a cover letter both use sentences. Only one of them has a reader who has to pick someone."
        memoryHook="Stakes equal: who reads it, what they decide, what changes after."
        why="Most students learn writing where the reader is a teacher who has to read it anyway. The cover letter, the personal statement, the application: those readers do not have to read past sentence two. Stakes change the math. Stakes are why specifics matter more than polish."
      />

      <Subhead n={2} title="What a high-stakes paragraph has to do" className="mt-12" />
      <p className="mt-4 text-text/90">
        Three jobs. In order. Each one fails if the one before it failed.
      </p>
      <JobsGrid />

      <Subhead n={3} title="Where AI helps and where AI hurts" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is excellent at two parts of this writing and bad at the parts that
        actually decide whether you get picked. Knowing the split is the lesson.
      </p>
      <SplitGrid />

      <Subhead n={4} title="The cover-letter chain" className="mt-12" />
      <p className="mt-4 text-text/90">
        The Week 3 chain still runs. The steps shift because the work is a
        letter, not an essay.
      </p>
      <div className="mt-6 rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
          The chain, for a cover letter
        </p>
        <ol className="mt-3 space-y-3 text-text/90">
          <li><span className="font-mono text-xs text-amber mr-2">01.</span> Decide: pick the one reason this firm, school, or program should pick you. One. Not three.</li>
          <li><span className="font-mono text-xs text-amber mr-2">02.</span> Decompose: what does that one reason need to be believable? Usually a moment, a number, a small piece of work you can name.</li>
          <li><span className="font-mono text-xs text-amber mr-2">03.</span> Source: from your life. Not from the internet. Find the moment. Write it down rough.</li>
          <li><span className="font-mono text-xs text-amber mr-2">04.</span> Draft: hand AI the moment and the role. Ask for a structure. Keep the moment in your words.</li>
          <li><span className="font-mono text-xs text-amber mr-2">05.</span> Audit: every sentence has to either say a specific thing or set up a specific thing. Cut every line that could appear in someone else&apos;s letter.</li>
        </ol>
      </div>

      <Subhead n={5} title="The cut rule for high-stakes writing" className="mt-12" />
      <p className="mt-4 text-text/90">
        If a sentence in your cover letter would also be true in the cover letter
        of every other applicant: cut it. That sentence is taking up a slot that
        a specific sentence could have.
      </p>
      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          The cut test
        </p>
        <p className="mt-3 text-text/90">
          Take any sentence. Add &ldquo;... and so does every other applicant&rdquo;
          to the end of it. If the sentence still makes sense, cut it. Replace it
          with something only you could say.
        </p>
        <p className="mt-3 text-sm text-text/80 italic">
          Example: &ldquo;I am hardworking and motivated... and so does every
          other applicant.&rdquo; Cut. Replace with the four Saturdays at the
          construction site.
        </p>
      </div>
    </Section>
  );
}

const JOBS = [
  {
    n: '01',
    name: 'Get read past sentence two',
    body: 'Most readers decide whether to keep reading in the first two sentences. A cover letter that opens with "I am writing to express interest" tells the reader nothing they cannot already see from the application form. Specifics earn the third sentence.',
    accent: 'amber' as const,
  },
  {
    n: '02',
    name: 'Make the reader believe one thing',
    body: 'A cover letter is not a list of your qualifications. It is one argument for why you in particular fit this opportunity in particular. The whole letter exists to make the reader believe that one thing. Pick it before you write.',
    accent: 'emerald' as const,
  },
  {
    n: '03',
    name: 'Be defendable in an interview',
    body: 'Whatever you wrote will get asked about. If you wrote "I led a team of five," they will ask what the team did. If AI wrote that and you did not, you have a problem. Every claim has to trace back to a real thing you can talk about for five minutes.',
    accent: 'amber' as const,
  },
];

function JobsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {JOBS.map((j) => {
        const isEm = j.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-amber/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-amber/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-amber';
        return (
          <div key={j.n} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <div className="flex flex-wrap items-baseline gap-3">
              <span className="font-mono text-xs text-muted">Job {j.n}</span>
              <h4 className={`text-lg font-semibold ${labelCls}`}>{j.name}</h4>
            </div>
            <p className="mt-3 text-text/90">{j.body}</p>
          </div>
        );
      })}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Structure: openings, middles, closings that follow a real letter shape.',
      'Tightening: cutting word count without losing meaning.',
      'Tone: matching the formality of the program you are writing to.',
      'Spelling, grammar, parallel structure, weak verbs.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'The opening sentence: AI defaults to the generic opener that everyone uses.',
      'The reason you in particular: AI does not know what makes you different.',
      'The moment: AI invents details that are not yours and cannot be defended.',
      'The voice: AI smooths out the parts that sound like a real person.',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span>{it}</span>
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Pick the opener that gets read" title="Three application openings. Pick the one a reader would keep reading.">
      <p className="mt-6 text-text/90">
        Each drill shows two opening paragraphs for the same application. One was
        finished by AI without any of the five moves. One was finished by AI
        after the writer ran the moves. Pick which gets read past sentence two.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <OpenerDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  context: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    context:
      'Opening of a personal statement for a six-week summer biology program at a research university.',
    optionA:
      '“I have always been fascinated by science. From a young age, I have been drawn to the natural world and the way that biological systems work together to create life. I am eager to deepen my knowledge of biology through this program.”',
    optionB:
      '“The thing I keep coming back to is the catfish in the creek behind my grandmother\'s house in Tyler. After the August flood the water came up six feet and dropped back in two days. The catfish were fine. The minnows were gone. I want to spend six weeks learning why.”',
    correct: 'B',
    verdict:
      'Option B opens with one moment, one place, one observation, and one specific question. The reader knows by sentence four that the writer notices things in the actual world and brings real questions to a science program. Option A could be the opening of any biology applicant in any year at any program. The reader has nothing to keep reading for.',
  },
  {
    n: '02',
    context:
      'Opening of a cover letter for a part-time job at an independent bookstore.',
    optionA:
      '“Saturday mornings at the store on Henderson, I watch the same three people come in for the same three sections: the woman who buys exactly one mystery, the dad who reads the back of every cookbook, the kid who hides in the YA shelf. I want to be the person who knows what to hand each of them next.”',
    optionB:
      '“I am writing to apply for the part-time bookseller position at your store. I am a passionate reader who would love to share my love of books with customers. I believe that working at an independent bookstore would be a rewarding and fulfilling experience.”',
    correct: 'A',
    verdict:
      'Option A names a specific store, a specific day, and three specific customers the writer has already noticed. The owner reading this letter recognizes those customers and now wants to know what this person would hand each of them. Option B is the cover letter every bookstore in America gets every month. Nothing in it could only be true of the writer.',
  },
  {
    n: '03',
    context:
      'Opening of a college essay responding to the prompt “describe something that has changed how you think.”',
    optionA:
      '“An experience that has truly transformed my perspective is volunteering at the local food bank. Through this experience, I have learned about the importance of community, empathy, and giving back to those in need. It has changed me as a person in many meaningful ways.”',
    optionB:
      '“Box 14 at the food bank is canned green beans. I stacked them for two hours on a Thursday in November before I noticed that everyone who took one also took crackers, never bread. I asked Mr. Reyes why. He said the people on box 14 mostly do not have a stove that works.”',
    correct: 'B',
    verdict:
      'Option B answers the prompt with one moment that earned the change. The reader can see Box 14, can see crackers next to green beans, can hear Mr. Reyes. The change in thinking is implied by the specifics, which is how change actually shows up in writing. Option A names the change but never proves it. AI can write Option A all day. Only the writer can write Option B.',
  },
];

function OpenerDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The context
      </p>
      <p className="mt-3 text-text/90">{drill.context}</p>

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which opener gets read past sentence two?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span
                  className="mt-2 block italic"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That opener does not earn the next sentence. See the one that does
              below.
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The opener that gets read' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90">{drill.verdict}</p>
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Stakes in one sentence." answer="What the reader can give you or take from you: who reads it, what they decide, what changes after." />
        <QuickCheckItem n="Q2" q="The three jobs a high-stakes opening paragraph has to do, in order." answer="Get read past sentence two. Make the reader believe one thing. Be defendable in a conversation about it later." />
        <QuickCheckItem n="Q3" q="The cut test, in one sentence." answer="If a sentence in your letter would also be true in every other applicant&rsquo;s letter, cut it and replace it with something only you could say." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on a cover letter. Two things AI hurts." answer="Helps: structure, tightening, tone, grammar. Hurts: the opening sentence, the reason you in particular, the moment, the voice." />
        <QuickCheckItem n="Q5" q="Why does a cover letter need to be defendable in conversation?" answer="Because the next step after the letter is usually an interview, and every claim in the letter is a possible question. If AI wrote a claim you cannot back up, you fail the interview your letter just earned you." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The high-stakes paragraph map.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 5.1 · Lock card · The cover letter and the personal statement
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          When the reader has to choose, one specific second beats four polished
          paragraphs of nothing.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <LockTile n="01" name="Job 1" body="Get read past sentence two. Open with a specific second from your actual life." />
          <LockTile n="02" name="Job 2" body="Make them believe one thing. Pick the one reason. Cut every sentence that does not serve it." />
          <LockTile n="03" name="Job 3" body="Be defendable. Every claim has to trace back to a real moment you can talk about for five minutes." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The cut test
          </p>
          <p className="mt-2 text-text/90">
            Add &ldquo;... and so does every other applicant&rdquo; to any
            sentence in your letter. If the sentence still makes sense, cut it
            and replace it with something only you could say.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The split
          </p>
          <p className="mt-2 text-text/90">
            AI helps with structure, tightening, tone, grammar. AI hurts on the
            opening, the reason you in particular, the moment, the voice. You do
            the second list. AI does the first.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          Stakes change which sentences earn the next one.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 5.2
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            The ask email: writing to a teacher, professor, or advisor
          </h3>
          <p className="mt-3 text-sm text-text/85">
            Rec letters. Meeting requests. Research spots. Grade appeals. One
            reader who already knows who you are and has to decide whether to
            spend forty-five minutes on your ask. Vocab: Grounding. The
            three-line opener that gets a reply.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 5.1 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From Week 4',
    'High-stakes writing',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
